<script setup>
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

function isEmptyValue(dataValue) {
  return (
      dataValue === []
      || dataValue === {}
      || dataValue === ''
      || dataValue === null
  )
}
</script>

<script>
import isEmpty from 'lodash/isEmpty';

export default {
  inheritAttrs: true,
  name: "DpReadValue",
  props: {
    dataKey: {type: String, required: true},
    dataValue: {type: [String, Object, Array, Number]},
    dataKeyStyle: {type: Object},
    dataValueStyle: {type: Object},
    inline: {type: Boolean, default: false},
    padding: {type: Boolean, default: false},
    paddingLeft: {type: Boolean, default: false},
    paddingRight: {type: Boolean, default: false},
    copyToClipboard: {type: Boolean, default: false},
    bdo: {type: String}
  },
  computed: {
    isEmpty() {
      return isEmpty;
    }
  }
}
</script>

<template>
  <div
      :class="['dp-simple-card__read-item', {'display-inline': inline, 'padding-left': paddingLeft, 'padding-right': paddingRight || padding}]"
      v-bind="$attrs">
    <div class="dp-simple-card__read-key" :style="dataKeyStyle">{{dataKey}}</div>
    <slot>
      <div class="dp-simple-card__read-value" :style="dataValueStyle" v-if="isEmptyValue(dataValue)"><span style="color: var(--use-theme__event__info__font-color);">{{t('noEntry.label')}}</span>
      </div>
      <div class="dp-simple-card__read-value" :style="dataValueStyle" v-else-if="typeof dataValue === 'string' || typeof dataValue === 'number'">
        <bdo v-if="!isEmpty(bdo)" :dir="bdo">{{
            dataValue
          }}</bdo>
        <template v-else>{{
            dataValue
          }}</template>
      </div>
      <div class="dp-simple-card__read-value" :style="dataValueStyle" v-else-if="Array.isArray(dataValue)">
        <ul>
          <li v-for="(item, index) in dataValue" :key="index">{{item}}</li>
        </ul>
      </div>
      <div class="dp-simple-card__read-value" :style="dataValueStyle" v-else-if="typeof dataValue === 'object' || typeof dataValue === 'number'">
        <div v-if="dataValue.type !== undefined && dataValue.type === 'media/image'"
             :style="{height: dataValue.meta.displayAt.height, width: dataValue.meta.displayAt.width}">
          <div class="dp-simple-card__read-value__image" :style="{'background-image': `url(${dataValue.url})`}"></div>
        </div>
        <ul v-else>
          <li v-for="(itemKey, index) in Object.keys(dataValue)" :key="index">
            <template v-if="dataValue.hasOwnProperty(itemKey)">
              <span style="color: var(--use-theme__event__info__font-color);">{{itemKey}}</span>: {{dataValue[itemKey]}}
            </template>
            <template v-else>
              <i style="color: var(--use-theme__event__info__font-color);">{{t('undefined.label')}}</i>
            </template>
          </li>
        </ul>
      </div>
      <div class="dp-simple-card__read-value" :style="dataValueStyle" v-else-if="typeof dataValue === 'boolean'">
        <span v-if="dataValue === true" class="dp-simple-card__read-value__boolean--true">{{t('active.label')}}</span>
        <span v-if="dataValue === false" class="dp-simple-card__read-value__boolean--false">{{t('inactive.label')}}</span>
      </div>
      <div class="dp-simple-card__read-value" :style="dataValueStyle" v-else><i style="color: var(--use-theme__event__info__font-color);">{{t('undefined.label')}}</i></div>
    </slot>
  </div>
</template>

<style lang="scss">

.dp-simple-card__read-item {
  padding: 1em 0;

  &.display-inline {
    display: inline-block;
  }

  &.padding-left {
    padding-inline-start: 2em;
  }

  &.padding-right {
    padding-inline-end: 2em;
  }

  .dp-simple-card__read-key {
    font-size: 12px;
    color: #a3a3a3;
    padding-bottom: .5em;

  }

  .dp-simple-card__read-value {
    color: var(--use-theme__font-color);

    .dp-simple-card__read-value__boolean {
      border-width: 2px;
      border-style: solid;
      padding: 0.2em 0.5em;
      border-radius: 4px;
    }
    .dp-simple-card__read-value__boolean--false {
      @extend .dp-simple-card__read-value__boolean;
      color: var(--use-theme__event__error__font-color);
      background-color: var(--use-theme__event__error__background-color);
      border-color: var(--use-theme__event__error__background-color);
    }
    .dp-simple-card__read-value__boolean--true {
      @extend .dp-simple-card__read-value__boolean;
      color: var(--use-theme__event__success__font-color);
      background-color: var(--use-theme__event__success__background-color);
      border-color: var(--use-theme__event__success__background-color);
    }

    .dp-simple-card__read-value__image {
      -webkit-border-radius: 6px;
      -moz-border-radius: 6px;
      border-radius: 6px;
      background-position: center;
      background-size: cover;
      width: 100%;
      height: 100%;
    }

    ul {
      margin: 0;
      padding: 0;
      list-style: none;

      li {
        margin-inline-start: 1.5rem;

        &:before {
          margin-inline-start: -1.5rem;
          margin-inline-end: 0.5rem;
          content: '—'
        }
      }
    }

  }

}

</style>

<i18n>
{
  "ar-AE": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "ممنوع الدخول"
    }
  },
  "ar-EG": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "ممنوع الدخول"
    }
  },
  "ar-SA": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "ممنوع الدخول"
    }
  },
  "el-GR": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Απαγορεύεται η είσοδος"
    }
  },
  "el-CY": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Απαγορεύεται η είσοδος"
    }
  },
  "de-DE": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Kein Eintrag"
    }
  },
  "de-AT": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Kein Eintrag"
    }
  },
  "de-CH": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Kein Eintrag"
    }
  },
  "en-US": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "No entry"
    }
  },
  "en-GB": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "No entry"
    }
  },
  "it-IT": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Vietato l'ingresso"
    }
  },
  "fr-FR": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Entrée interdite"
    }
  },
  "es-ES": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "No hay entrada"
    }
  },
  "pl-PL": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Brak wejścia"
    }
  },
  "nl-NL": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Geen toegang"
    }
  },
  "bg-BG": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Не е вход"
    }
  },
  "bs-BA": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Zabranjen ulaz"
    }
  },
  "da-DK": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Ingen adgang"
    }
  },
  "et-EE": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Sissepääs puudub"
    }
  },
  "fi-FI": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Ei sisäänkäyntiä"
    }
  },
  "he-IL": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "אין כניסה"
    }
  },
  "ga-IE": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Gan iontráil"
    }
  },
  "ja-JP": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "立入り禁止"
    }
  },
  "hr-HR": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Zabranjen ulaz"
    }
  },
  "lv-LV": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Iebraukt aizliegts"
    }
  },
  "lt-LT": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Nėra įėjimo"
    }
  },
  "pt-PT": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Entrada proibida"
    }
  },
  "ro-RO": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Nicio intrare"
    }
  },
  "ru-RU": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Нет входа"
    }
  },
  "sv-SE": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Ingen ingång"
    }
  },
  "sk-SK": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Zákaz vstupu"
    }
  },
  "cs-CZ": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Vstup zakázán"
    }
  },
  "hu-HU": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Belépni tilos"
    }
  },
  "fr-BE": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Entrée interdite"
    }
  },
  "fr-LU": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Entrée interdite"
    }
  },
  "mt-MT": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "L-ebda dħul"
    }
  },
  "sl-SI": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Ni vstopa"
    }
  },
  "tr-TR": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Giriş yok"
    }
  },
  "tr-CY": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Giriş yok"
    }
  },
  "uk-UA": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "Kein Eintrag"
    }
  },
  "zh-CN": {
    "active": {
      "label": ""
    },
    "undefined": {
      "label": ""
    },
    "inactive": {
      "label": ""
    },
    "noEntry": {
      "label": "禁止入内"
    }
  }
}
</i18n>
